import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { createUseStyles } from "react-jss";
import { useHistory } from "react-router-dom";
import MuiDataTable from "../../../components/Table/MuiDataTable/MuiDataTable";
import { MATTER, MATTERROUTE, PRODUCT_EXPORT} from "../../../constants/constants";
import {  formSearchQuery, getAllowedActionsList } from "../../../helpers/tableUtils";
import ExportModal from "../../../components/Modal/ExportModal/ExportModal";
import { getProductListingConfig } from "../../../components/Table/Columns/ProductListingColumns";
import { MATTER_SERVICE } from "../../../constants/baseURLs";
import { getService, getServiceWithResponseType } from "../../../axios/axios";
import { SOMETHING_WENT_WRONG } from "../../../constants/messages";
import { useSnackbar } from "@livingdesign/react";
import { getExportedData } from "../../../helpers/tableUtils";
import { Operations } from "../../../axios/operations";
import { ROWS_PER_PAGE_PRODUCTS } from "../../../constants/tableConfigs";

/**
 * styles used in the component.
 */
const useStyles = createUseStyles({
  dataTableWrap: {
    margin: "0",
    '& table > thead > tr > th.MuiTableCell-head': {
      color: '#000000',
      fontWeight: 'bold'
    },
    '& table > thead > tr > th.MuiTableCell-head button': {
      color: '#000000',
      fontWeight: 'bold'
    }
  }
});

const MatterProductList = ({matterId, matterDetail, currentMatterTab}) => {
  const classes = useStyles();
  const history = useHistory();
  const accessibility = useSelector((state) => state?.accessibility?.applicationAccess?.products);
  const { addSnack } = useSnackbar();
  const [ matterProductData, setMatterProductData ] = useState({});
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectRowsData, setSelectRowsData] = useState([]);
  const [rowsData, setRowsData] = useState([]);
  const [currentPageExport, setCurrentPageExport] = useState(false);
  const [exportSelection, setExportSelection] = useState("currentPage")
  const [searchQuery, setSearchQuery] = useState("");
  const [filterQuery, setFilterQuery] = useState("");
  const [sortQuery, setSortQuery] = useState("&order=lastUpdatedTs desc");
  const [page, setPage] = useState(0);
  const [rowsPerPageProducts, setRowsPerPageProducts] = useState(ROWS_PER_PAGE_PRODUCTS);

  const defaultFilterCondition = `${formSearchQuery(
        "matterId",
        Operations.EQ,
        matterId,
        'id'
      )} ${formSearchQuery(
        "isActive",
        Operations.EQ,
        true,
        'boolean',
      )}`;

  const getProductData = (rowsPerPageProducts, page) => {
    setLoading(true);
    const paginationQuery = formPaginationQuery(rowsPerPageProducts, page);
    const csv = "&csv=false";    
    getService(MATTER_SERVICE, `/search/matter-products/v1?filter=${defaultFilterCondition} and ${filterQuery} ${searchQuery} ${sortQuery} ${paginationQuery} ${csv}`)
    // getService(MATTER_SERVICE, `/matter-products/v1/matters/${matterId}`)
    .then((res) => {
      setMatterProductData(res?.data)
    })
    .catch((error) => {
      addSnack({
        message: SOMETHING_WENT_WRONG
      });
    })
    .finally(()=>{
      setLoading(false);
    });
  };

  useEffect(() => {
      getProductData(rowsPerPageProducts, page);
  }, [sortQuery, searchQuery, filterQuery]);
  
  const actionItems = [
    {
      "actionId": 0,
      "label": "Edit",
      "isAboveDivider": true,
      "showAction": true,
    },
    {
    "actionId": 1,
    "label": "Remove",
    "isAboveDivider": true,
    "showAction": true,
    }
  ]

  const FILTER_CONFIGS = [
      {
        colKey: "productName",
        type: "textField",
        label: "Product Name",
        defaultOpen: true,
      },
      {
        colKey: "productSupplier",
        type: "textField",
        label: "Product Supplier",
        defaultOpen: true,
      },
      {
        colKey: "manufacturer",
        type: "textField",
        label: "Manufacturer",
        defaultOpen: true,
      },
      {
        colKey: "itemNumber",
        type: "textField",
        label: "Item Number",
        defaultOpen: true,
      },
      {
        colKey: "upcNumber",
        type: "textField",
        label: "UPC Number",
        defaultOpen: true
      }
    ];

  const COLUMNS = getProductListingConfig();
  const [productColumns, setProductColumns] = useState(COLUMNS);

  const handleRowClick = (rowData, rowMeta) => {
    const productDetails = matterProductData?.matterProducts[rowMeta?.rowIndex]
    const breadcrumbValues= [
      {
        label: MATTER,
        path: MATTERROUTE,
        currentMatterTab
      },
      {
        label: `${matterDetail?.matterNumber}`,
        path: (`matter/${matterId}`),
        currentMatterTab,
      }, 
      { label: rowData[0]}
    ]
    history.push(`/product/${productDetails?.id}/view`, {currentMatterTab: currentMatterTab, matterId: matterId, breadcrumbValues: breadcrumbValues, productDetails: productDetails });
  }

  const handleServerSideSearch = (searchText) => {
    let query = "";
    if (searchText !== "") {
      COLUMNS?.forEach((column, index) => {
        if(column?.options?.display && column?.operation) {
          let colName = column?.name
          const colQuery = formSearchQuery(colName, column?.operation, searchText, column?.type);
          if (colQuery !== "") {
            query += index > 0 ? " OR " + colQuery : colQuery;
          }
        }
      });
    }
    setSearchQuery(query);
  };

  const handleServerSideFilter = (filterQuery) => {
    setFilterQuery(filterQuery);
  };

  const handleServerSideSort = ({ name, direction }) => {
     const sortQuery = `&order=${name} ${direction}`;
     setSortQuery(sortQuery);
  };

  const formPaginationQuery = (rowsPerPage, pageNumber) => (`&offset=${rowsPerPage * pageNumber}&limit=${rowsPerPage}`)

  const handleServerSidePagination = ({ rowsPerPage, page: pageNumber }) => {
    let prevPage = page;
    if(rowsPerPageProducts !== rowsPerPage) {
      pageNumber = 0;
      prevPage = 0;
    }
    setPage(pageNumber);
    setRowsPerPageProducts(rowsPerPage);
    getProductData(rowsPerPage, pageNumber);
  };

  const productListTableCustomOptions = {
    downloadOptions: {
      filename: "DOWNLOAD_FILENAME",
    },
    totalCount: matterProductData?.count,
    rowsPerPage: rowsPerPageProducts,
    page: page,
    serverSide: true,
    filterConfigs: FILTER_CONFIGS,
    onServerSidePagination: handleServerSidePagination,
    onServerSideSearch: handleServerSideSearch,
    onServerSideFilter: handleServerSideFilter,
    onServerSideSort: handleServerSideSort,
  };

  const onCancelBtnClk = () => {
    setExportLoading(false)
    setModalOpen(false)
  }

  const handleExport = () => {
    setModalOpen(true);
  };

  const onClickCreate = () => {
    setExportLoading(true);
    const csv = "&csv=true";
    const paginationQuery = formPaginationQuery(rowsPerPageProducts, page);
    let exportQuery = `filter=${defaultFilterCondition} and ${filterQuery} ${sortQuery} ${searchQuery} ${paginationQuery}`;
    if (exportSelection === 'currentSelection') {
      const result = rowsData?.map(a => a?.id);
      exportQuery = `filter=${formSearchQuery('id', Operations.IN, result, 'array')}`;
    } else if (exportSelection === 'currentPage') {
      const result = matterProductData?.matterProducts?.map(a => a?.id);
      exportQuery = `filter=${formSearchQuery('id', Operations.IN, result, 'array')}`;
    }
    getServiceWithResponseType(
      MATTER_SERVICE,
      `/search/matter-products/v1?${exportQuery}${csv}`,
      "arraybuffer"
    )
    .then((response) => {
      const data = response?.data;
      const contentType = response?.headers["content-type"];
      setModalOpen(false);
      getExportedData(data, contentType,'product-list');
    })
    .catch((error) => {
      setModalOpen(false);
      addSnack({
        message: error.message,
      });
    })
    .finally(() => {
      setExportLoading(false);
    });    
  }
    

  const handleRowSelectionChange = (currentSelect, allSelected, selectRowsData) => {
    const data = matterProductData?.matterProducts;
    const result = allSelected?.map(item => {
      return data && data[item?.dataIndex]
    });
    setSelectRowsData(selectRowsData);
    setRowsData(result);  }

  const resetSelectedRows = () => {
    setSelectRowsData([]);
    setRowsData([]);
  }

  return (
    <>
      <div className={classes.dataTableWrap}>
        <MuiDataTable
          data={matterProductData?.matterProducts}
          columns={productColumns}
          setTableColumns={setProductColumns}
          customOptions={productListTableCustomOptions}
          selectableRowsType={"multiple"}
          onRowClick={handleRowClick}
          groupFilter={false}
          exportLoading={exportLoading}
          onExportClick={handleExport}
          selectedRows={selectRowsData}
          onRowSelection={handleRowSelectionChange}
          allowedActions={getAllowedActionsList(accessibility)}
          loading={loading}
          showRowsCounter={false}
          currentPageExport={currentPageExport}
					resetSelectedRows={() => resetSelectedRows()}
        />
      </div>
      <ExportModal
				title={PRODUCT_EXPORT}
				mode={modalOpen}
				onCancel={onCancelBtnClk}
				onCreate={onClickCreate}
				rowsData={rowsData}
				exportSelection={exportSelection}
        exportLoading={exportLoading}
        setExportSelection={setExportSelection}
				currentPageExport={currentPageExport}
        showExportAllOption={true}
				setCurrentPageExport={setCurrentPageExport}
			/>
    </>
  );
}

export default MatterProductList;